import { apiClient } from '../client';

/**
 * Cursor paging of the entity lists (DX-2, `POST /api/entities/:entity/page`).
 * The cursor is opaque and bound to the filter and sort it was issued for:
 * send the same ones with it. `total` only comes back with include_total.
 */
export interface EntityPage<T> {
  items: T[];
  next_cursor: string | null;
  total?: number;
}

export interface EntityPageRequest {
  filter?: Record<string, unknown>;
  /** created_date or updated_date, "-" for descending (default -created_date). */
  sort?: string;
  /** Default 50, at most 200. */
  limit?: number;
  cursor?: string;
  include_total?: boolean;
}

export type PagedEntity = 'ParallelCall' | 'ProspectQueue';

export const PAGE_MAX_LIMIT = 200;

export function fetchEntityPage<T>(entity: PagedEntity, body: EntityPageRequest, signal?: AbortSignal) {
  return apiClient.post<EntityPage<T>>(`/entities/${entity}/page`, body, { signal });
}

/**
 * Reads every page of a filter, up to `cap` rows (an export, an analysis).
 * Never truncates silently (T-d3): `capped` says rows were left out, `total`
 * how many matched.
 */
export async function fetchAllPages<T>(
  entity: PagedEntity,
  body: Pick<EntityPageRequest, 'filter' | 'sort'>,
  options: { cap: number; signal?: AbortSignal; onProgress?: (loaded: number, total: number | null) => void },
): Promise<{ items: T[]; total: number; capped: boolean }> {
  const items: T[] = [];
  let cursor: string | undefined;
  let total: number | null = null;
  for (;;) {
    const limit = Math.max(1, Math.min(PAGE_MAX_LIMIT, options.cap - items.length));
    const page = await fetchEntityPage<T>(entity, { ...body, limit, ...(cursor ? { cursor } : { include_total: true }) }, options.signal);
    if (typeof page.total === 'number') total = page.total;
    items.push(...page.items);
    options.onProgress?.(items.length, total);
    if (!page.next_cursor) return { items, total: total ?? items.length, capped: false };
    if (items.length >= options.cap) return { items, total: total ?? items.length, capped: true };
    cursor = page.next_cursor;
  }
}
